import { Component, OnInit, ViewChild } from '@angular/core';
import { UserApiService } from 'src/app/services/api/user.api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { CompanyApiService } from 'src/app/services/api/companies.api.service';
import { countries } from '../../../assets/data/countries';
import { getRoles } from '../../../assets/data/roles';
import { NgSelectComponent } from '@ng-select/ng-select';
import { User } from 'src/app/classes/User.class';
import { Role } from 'src/app/classes/Role.class';
import { Company } from 'src/app/classes/Company.class';
import { getAllLanguages, getAllLanguagesEN } from 'src/assets/data/languages';
import { Language } from 'src/app/classes/Language.class';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'user-detail-screen',
  templateUrl: './user-detail.screen.html',
  styleUrls: ['./user-detail.screen.scss'],
})
export class UserDetailScreenComponent implements OnInit {
  @ViewChild(NgSelectComponent) cityNgSelectComponent: NgSelectComponent;

  @ViewChild(NgSelectComponent) companyNgSelectComponent: NgSelectComponent;

  user: User;
  currentAdmin: User;
  countries: string[] = [];
  companies: { data: Company[] };
  selectableCompanies: Company[] = [];
  cities: Array<string> = [];
  country: string;
  city: string;
  roles: Role[] = [];
  emailSent = false;
  modalText = '';
  error = false;
  errorUser = false;
  disableForm = false;
  awaitingActivation = false;
  onlyMowaAdmin = false;
  languages: Language[];

  private userCompany: number;

  constructor(
    private userApiService: UserApiService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private _location: Location,
    private companyApiService: CompanyApiService,
    private translate: TranslateService,
  ) {}

  async ngDoCheck() {
    if (this.translate.getDefaultLang() === 'en') {
      this.languages = getAllLanguagesEN();
    } else {
      this.languages = getAllLanguages();
    }
  }

  async ngOnInit() {
    this.currentAdmin = await this.userApiService.getAdmin();
    this.activatedRoute.params.subscribe(async params => {
      if (params && params.userId != 'null') {
        this.companies = await this.companyApiService.getCompanies();
        this.user = await this.userApiService.getUser(params.userId);
        this.userCompany = this.user.companyId;
        this.countries = this.formatCountry(this.companies.data, this.countries);

        this.setCompany();
        this.roles =
          this.user.role === 'super_admin'
            ? getRoles(true, this.translate.getDefaultLang())
            : getRoles(false, this.translate.getDefaultLang());

        if (!this.mowaAdmin && (!this.user.magentoCustomerId || !this.user.requestForCompanyName)) {
          this.disableForm = true;
          this.setModalText('response.sync_user');
        } else if (this.mowaAdmin) {
          this.disableForm = true;
          this.onlyMowaAdmin = true;
          this.setModalText('response.mowa_admin');
        }
      }
    });
  }

  private formatCountry(companyData: Company[], arr: string[]): string[] {
    companyData.forEach(company => {
      if (!arr.includes(company.country)) arr.push(company.country);
    });
    const newArr: Array<any> = [];
    arr.map(iso => {
      countries.filter(e => (e.code === iso ? newArr.push(e) : ''));
    });
    return newArr;
  }

  private async checkIfCompanyHasSuperAdmin(companyId: number): Promise<boolean> {
    const allCompanyUser = await this.userApiService.getUsersForCompany(companyId);
    let check = false;
    allCompanyUser.forEach(user => {
      if (user.id !== this.user.id && user.role === 'super_admin') check = true;
    });
    return check;
  }

  filterCities(isInit = false) {
    this.city = null;
    if (!isInit) this.user.companyId = null;
    const companies = this.companies.data.filter(e => {
      return e.country == this.country;
    });
    const cities = [];
    for (let i = 0; i < companies.length; i++) {
      if (!cities.includes(companies[i].city)) cities.push(companies[i].city);
    }
    this.cities = cities;
  }

  filterCompanies(isInit = false) {
    if (!isInit) this.user.companyId = null;
    const companies = this.companies.data.filter(e => {
      return e.country == this.country && e.city == this.city;
    });
    this.selectableCompanies = companies;
  }

  private async setCompany() {
    let selectedCompany: Company = null;
    this.companies.data.filter(company => {
      if (company.id === this.user.companyId) selectedCompany = company;
    });
    this.country = selectedCompany.country;
    this.filterCities(true);
    this.city = selectedCompany.city;
    this.filterCompanies(true);
  }

  async forgotPasswordEmail() {
    if (this.user.email !== '') {
      const response = await this.userApiService.forgotPasswordEmail(this.user.id);
      if (response) {
        this.setModalText('response.email');
        this.emailSent = true;
        setTimeout(() => {
          this.closeModal();
        }, 2700);
      }
    }
  }

  async saveUser(user) {
    if (user.role === 'super_admin' && user.companyId !== this.userCompany) {
      this.error = await this.checkIfCompanyHasSuperAdmin(user.companyId);
      if (this.error) return;
    }
    if (user.awaitingActivation == 1 && this.awaitingActivation)
      this.changeUserActivationStatus(user);
    const response = await this.userApiService.saveUser(user);
    if (!response.code) {
      this.setModalText('response.save_user');
      this.emailSent = true;
      setTimeout(() => {
        this.closeModal();
        user.awaitingActivation !== 1
          ? window.location.reload()
          : this.router.navigateByUrl('/registration-request');
      }, 2700);
    } else {
      this.errorUser = true;
    }
  }

  private changeUserActivationStatus(user) {
    user.awaitingActivation = !this.awaitingActivation;
    user.active = this.awaitingActivation;
  }

  goBack() {
    this._location.back();
  }

  closeModal() {
    this.emailSent = false;
  }

  setModalText(val) {
    this.modalText = val;
  }

  goToCompanyDetailUrl(id) {
    return `/companies/${id}`;
  }

  userSameAsAdmin() {
    return this.user.email === this.currentAdmin.email;
  }

  get admin() {
    return this.user.role === 'admin';
  }

  get mowaAdmin() {
    return this.user.role === 'mowa_admin';
  }

  get permissionCheck() {
    return this.user.role === 'super_admin' && this.currentAdmin.role === 'admin';
  }
}
